import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function IndoorAirQualityPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whatWeTest = [
    {
      icon: 'ri-leaf-line',
      title: 'Mold & Microbial Growth',
      description: 'Air and surface sampling to identify mold species and contamination levels.',
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'HVAC & Ventilation',
      description: 'Airflow testing, filter evaluation, and system performance assessment.',
    },
    {
      icon: 'ri-drop-line',
      title: 'Moisture & Humidity',
      description: 'Moisture mapping, relative humidity testing, and leak detection.',
    },
    {
      icon: 'ri-flask-line',
      title: 'VOCs & Chemical Testing',
      description: 'Volatile organic compounds, formaldehyde, and other indoor air pollutants.',
    },
  ];

  const whoNeeds = [
    {
      title: 'Schools & Daycares',
      description: 'Protect children and staff with regular IAQ testing and mold prevention programs.',
    },
    {
      title: 'Healthcare Facilities',
      description: 'Critical air quality monitoring for hospitals, clinics, and senior living facilities.',
    },
    {
      title: 'Commercial Buildings',
      description: 'Tenant complaints, sick building syndrome, and ongoing IAQ management.',
    },
    {
      title: 'Housing Portfolios',
      description: 'Multi-site mold investigations and prevention programs for property managers.',
    },
    {
      title: 'Homeowners',
      description: 'Musty odors, visible mold, water damage, or unexplained health symptoms.',
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Initial Assessment',
      description: 'We review your concerns, building history, and visible conditions to plan the investigation.',
    },
    {
      step: '02',
      title: 'Testing & Sampling',
      description: 'Air samples, surface samples, moisture readings, and HVAC evaluation using calibrated equipment.',
    },
    {
      step: '03',
      title: 'Lab Analysis',
      description: 'Samples analyzed by accredited microbiology labs for mold species and spore counts.',
    },
    {
      step: '04',
      title: 'Report & Action Plan',
      description: 'Clear findings, source identification, and practical remediation recommendations.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Indoor Air Quality & Mold Testing
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Comprehensive indoor air quality testing and mold investigations to identify environmental health concerns. We help property owners, facility managers, and tenants understand air quality issues and develop practical remediation plans.
            </p>
            <Link
              to="/contact#contact-form"
              className="inline-block bg-[#f59e0b] text-white px-10 py-4 rounded-lg text-base font-semibold hover:bg-[#d97706] transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
            >
              Request Testing
            </Link>
          </div>
        </div>
      </section>

      {/* What We Test */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What We Test & Investigate</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our IAQ investigations use state-of-the-art sampling and analysis to identify the root cause.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatWeTest.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                  <i className={`${item.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Needs This */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Who Needs IAQ Testing?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Indoor air quality issues can affect any building—from single homes to large commercial portfolios.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whoNeeds.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Our Investigation Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We don't just test—we find the source and give you a clear path forward.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300 h-full">
                  <div className="text-5xl font-bold text-[#f59e0b]/20 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common IAQ Problems */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Common IAQ Problems We Solve</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-black mb-3">Musty Odors</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Persistent smells often indicate hidden mold or moisture problems behind walls or under floors.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-black mb-3">Water Damage</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                After leaks or flooding, we test for mold growth and develop remediation protocols.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-black mb-3">Poor Ventilation</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                HVAC issues, inadequate airflow, and high humidity can create chronic IAQ problems.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-black mb-3">Tenant Complaints</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Respiratory symptoms, allergies, or sick building syndrome require professional investigation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-black mb-3">Visible Mold</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We identify the mold type, extent of contamination, and underlying moisture source.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-black mb-3">Post-Remediation</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Clearance testing to verify that remediation was successful and the space is safe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose LCA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Why Choose LCA for IAQ & Mold?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-microscope-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Advanced Testing Methods</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                State-of-the-art sampling equipment and accredited laboratory analysis.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-search-eye-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Root Cause Analysis</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We don't just identify the problem—we find the source and prevent recurrence.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-building-4-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Portfolio Programs</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Prevention and response programs for schools, healthcare, and commercial portfolios.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#f59e0b] to-[#d97706] rounded-3xl p-12 text-center shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Concerned About Indoor Air Quality?
            </h2>
            <p className="text-xl text-white/95 mb-8 leading-relaxed">
              Get answers with professional testing and expert recommendations. We'll help you identify the problem and fix it.
            </p>
            <Link
              to="/contact#contact-form"
              className="inline-block bg-white text-[#f59e0b] px-10 py-4 rounded-lg text-base font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
            >
              Schedule Testing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
